import React, { useState } from "react";
import { Table, Button } from "react-bootstrap";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { formatPrice } from "../../../../shared/utils/formatPrice";
import { ReportAgentResponseV4 } from "../../../../services/cm-group-report/response/ReportAgentResponseV4";
import { IconMinus, IconPlus, IconArrowUp, IconArrowDown } from "@tabler/icons-react";

interface ReportAgentTableV4Props {
    data: ReportAgentResponseV4;
    orderBy: string;
    sort: 'asc' | 'desc';
    setOrderBy: (field: string) => void;
    setSort: (direction: 'asc' | 'desc') => void;
}

const ReportAgentTableV4: React.FC<ReportAgentTableV4Props> = ({ data, orderBy, sort, setOrderBy, setSort }) => {
    const [expandedRow, setExpandedRow] = useState<number | null>(null);

    const columns = [
        { label: "Agent Name", key: "agent_name" },
        { label: "Percentage", key: "percentage" },
        { label: "Room Night Sold", key: "room_nights" },
        { label: "Reservation", key: "reservations" },
        { label: "Avg. Nights", key: "avg_nights" },
        { label: "Avg. Booking Window", key: "booking_window" },
        { label: "Revenue (IDR)", key: "revenue" },
        { label: "OTA Com (IDR)", key: "ota_com" },
        { label: "Avg. Rate (IDR)", key: "avg_rate" },
        { label: "Cancellation Count", key: "cancel_count" },
    ];

    const childColumns = [
        { label: "Property", key: "property" }, // Menggunakan "property" sesuai data API terbaru
        { label: "Room Nights", key: "room_nights" },
        { label: "Reservations", key: "reservations" },
        { label: "Avg. Nights", key: "avg_nights" },
        { label: "Avg. Booking Window", key: "booking_window" },
        { label: "Revenue (IDR)", key: "revenue" },
        { label: "OTA Com (IDR)", key: "ota_com" },
        { label: "Avg. Rate (IDR)", key: "avg_rate" },
        { label: "Cancel Count", key: "cancel_count" },
    ];

    const handleSort = (key: string) => {
        if (orderBy === key) {
            setSort(sort === 'asc' ? 'desc' : 'asc');
        } else {
            setOrderBy(key);
            setSort('desc');
        }
    };

    const renderSortIcon = (key: string) => {
        if (orderBy !== key) return null;
        return sort === 'asc' ? <IconArrowUp size={14} /> : <IconArrowDown size={14} />;
    };

    const formatRowValue = (row: any, colKey: string) => {
        const value = row[colKey as keyof typeof row];
        if (["revenue", "avg_rate", "ota_com"].includes(colKey)) return formatPrice(Number(value || 0));
        if (colKey === "percentage") return `${Number(value || 0).toFixed(2)}%`; // Fungsi Number() akan menangani string dan number
        return value ?? "N/A";
    };

    const exportToPDF = () => {
        const doc = new jsPDF();
        doc.text("Report Agent Table V4", 14, 10);
        const head = [columns.map(col => col.label)];
        const body = data.data.map(row => columns.map(col => formatRowValue(row, col.key)));
        const foot = [columns.map(col => {
            if (col.key === 'agent_name') return 'Total';
            return formatRowValue(data.total, col.key);
        })];
        
        autoTable(doc, { head, body, foot });
        doc.save("ReportAgentTableV4.pdf");
    };

    const exportToExcel = () => {
        const header = columns.map(col => col.label);
        const body = data.data.map(row => columns.map(col => formatRowValue(row, col.key)));
        const footer = columns.map(col => {
            if (col.key === 'agent_name') return 'Total';
            return formatRowValue(data.total, col.key);
        });
        const wsData = [header, ...body, footer];

        const ws = XLSX.utils.aoa_to_sheet(wsData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Report Agent V4");
        const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
        saveAs(new Blob([excelBuffer], { type: "application/octet-stream" }), "ReportAgentTableV4.xlsx");
    };

    return (
        <div className="mt-4">
            <div className="mb-3 d-flex gap-2">
                <Button variant="primary" onClick={exportToPDF}>Export to PDF</Button>
                <Button variant="success" onClick={exportToExcel}>Export to Excel</Button>
            </div>
            <div className="table-responsive">
                <Table striped bordered hover>
                    <thead>
                        <tr>
                            <th></th>
                            {columns.map((col) => (
                                <th key={col.key} onClick={() => handleSort(col.key)} style={{ cursor: 'pointer' }}>
                                    {col.label} {renderSortIcon(col.key)}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {data?.data?.length > 0 ? (
                            data.data.map((row, rowIndex) => (
                                <React.Fragment key={rowIndex}>
                                    <tr>
                                        <td>
                                            <button className="btn btn-primary rounded-circle d-flex align-items-center justify-content-center p-0"
                                                style={{ width: "24px", height: "24px" }}
                                                onClick={() => setExpandedRow(rowIndex === expandedRow ? null : rowIndex)}>
                                                {rowIndex === expandedRow ? <IconMinus size={14} /> : <IconPlus size={14} />}
                                            </button>
                                        </td>
                                        {columns.map((col) => (
                                            <td key={col.key}>{formatRowValue(row, col.key)}</td>
                                        ))}
                                    </tr>
                                    {expandedRow === rowIndex && (
                                        <tr>
                                            <td colSpan={columns.length + 1} className="p-3 bg-light">
                                                <h6 className="ms-2">Details for {row.agent_name}</h6>
                                                <Table bordered size="sm">
                                                    <thead>
                                                        <tr>
                                                            {childColumns.map(cCol => <th key={cCol.key}>{cCol.label}</th>)}
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {row.children.map((child, childIndex) => (
                                                            <tr key={childIndex}>
                                                                {childColumns.map(cCol => <td key={cCol.key}>{formatRowValue(child, cCol.key)}</td>)}
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </Table>
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            ))
                        ) : (
                            <tr><td colSpan={columns.length + 1} className="text-center">No data available</td></tr>
                        )}
                    </tbody>
                    <tfoot>
                        <tr>
                            <td></td> 
                            {columns.map((col, index) => (
                                <td key={col.key}>
                                    <strong>
                                        {index === 0 ? "Total" : formatRowValue(data.total, col.key)}
                                    </strong>
                                </td>
                            ))}
                        </tr>
                    </tfoot>
                </Table>
            </div>
        </div>
    );
};

export default ReportAgentTableV4;